// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React, { type Node, useState } from 'react';
import { uniqueId } from 'lodash';

import Tooltip from '@zapier/design-system/src/components/display/Tooltip';
import {
  Animation,
  Colors,
  Shadows,
  Typography,
} from '@zapier/design-system/src/theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

import {
  type CssProp,
  type ShadowName,
} from '@zapier/design-system/src/flow-types';

/** @jsx isolateComponent */

type InputElement = HTMLInputElement | HTMLTextAreaElement;

export type InputEvent = SyntheticInputEvent<InputElement>;

type KeyboardEvent = SyntheticKeyboardEvent<InputElement>;

type RenderIconProps = {
  // Only one size supported for now
  iconSize: 24,
  isFocused: boolean,
  isHovered: boolean,
};

export type InputProps = {|
  /**
   * Identifies the currently active descendant of a composite component.
   * This is used when `TextInput` is part of a composite component,
   * such as `Typeahead`. Without this, some functionality doesn't
   * work well for screen readers.
   */
  ariaActiveDescendant?: string,
  /**
   * Identifies the element whose contents or presence are controlled by
   * this component.
   * This is used when `TextInput` is part of a composite component,
   * such as `Typeahead`. Without this, some functionality doesn't
   * work well for screen readers.
   */
  ariaControls?: string,
  /**
   * Accessibility label that describes this component. This should be supplied
   * if no corresponding `label` element is used.
   */
  ariaLabel?: string,
  /**
   * Accessibility label referencing the ID of an element which labels this
   * component.
   */
  ariaLabelledBy?: string,
  /**
   * Accessibility label referencing the ID of an element which describes this
   * component.
   */
  ariaDescribedBy?: string,
  /**
   * True if user input is required before a form can be submitted.
   */
  ariaRequired?: boolean,
  /**
   * Whether the field can be autocompleted by the browser,
   * and if so what type of information should be autocompleted.
   * See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  autoComplete?: string,
  /**
   * Whether the input should be focused when it's initially rendered.
   */
  autoFocus?: boolean,
  /**
   * Pass in a value to determine which side of the button should be rounded.
   * Good for when the input needs to be flush against a flat surface
   */
  borderRadiusSide?: 'left' | 'right',
  /**
   * Indicates whether the icon that renders on the left of the `Input`
   * can be clicked. Set to `false` if clicking it should pass through
   * to the underlying rendered `children`.
   */
  canClickIconBefore?: boolean,
  /**
   * Indicates whether the icon that renders on the right of the `Input`
   * can be clicked. Set to `false` if clicking it should pass through
   * to the underlying rendered `children`.
   */
  canClickIconAfter?: boolean,
  /**
   * Color theme of the input.
   */
  color: 'grey100' | 'white',
  /**
   * The text that renders in a `Tooltip` when the input `isDisabled`.
   * Only renders when `isDisabled`.
   */
  disabledText?: string,
  /**
   * HTML `id` attribute for the element, used to associate it with a `label`.
   */
  id?: string,
  /**
   * Disables the input and makes it non-interactive.
   */
  isDisabled?: boolean,
  /**
   * Indicates whether the field has an error.
   */
  isErrored?: boolean,
  /**
   * Whether the input can only be read from. Contrast this with
   * `isDisabled`, which completely disables the input, while
   * `isReadOnly` leaves the input functional.
   */
  isReadonly?: boolean,
  /**
   * Indicates whether the field is required.
   */
  isRequired?: boolean,
  /** Optional max length for text inputs */
  maxLength?: number,
  /**
   * HTML name attribute, typically used for the key that will
   * be sent to the API.
   */
  name?: string,
  /**
   * Callback executed when the input is blurred.
   */
  onBlur?: (event: InputEvent, meta?: any) => void,
  /**
   * Callback executed when the input's value is changed.
   */
  onChange?: (event: InputEvent) => void,
  /**
   * Callback executed when the input is clicked.
   */
  onClick?: (event?: InputEvent) => void,
  /**
   * Callback executed when the input is focused.
   */
  onFocus?: (event: InputEvent) => void,
  /**
   * Callback executed when a key is pressed and the input is focused.
   */
  onKeyDown?: (event: KeyboardEvent) => void,
  /**
   * A hint to the user of what can be entered in the control.
   * **Note that this is not a sufficient label for accessibility!**
   * Be sure to either associate this input with a `label` element
   * or use the `ariaLabel` prop!
   */
  placeholder?: string,
  /**
   * Function that optionally returns an icon to render
   * on the left side of the input.
   */
  renderIconBefore?: (options: RenderIconProps) => ?Node,
  /**
   * Function that optionally returns an icon to render
   * on the right side of the input.
   */
  renderIconAfter?: (options: RenderIconProps) => ?Node,
  /**
   * Indicates role for the field, typically empty.
   */
  role?: string,
  /**
   * Indicates the type of shadow the input has.
   */
  shadow?: ShadowName,
  /**
   * The rendered size of the input.
   */
  size: 'small' | 'medium',
  /**
   * The actual value of the input.
   */
  value?: string,
|};

type ChildrenInputProps = {
  css?: CssProp,
  value?: string,
};

type DerivedState = {
  hasIconBefore: boolean,
  hasIconAfter: boolean,
  isFocused: boolean,
  isHovered: boolean,
};

type Props = {
  ...$Exact<InputProps>,
  /**
   * Function to render the input itself.
   */
  children: (ChildrenInputProps, DerivedState) => Node,
};

const sizeToHeight = {
  small: 40,
  medium: 50,
};

// Only one size supported for now.
const sizeToIconSize = {
  small: 24,
  medium: 24,
};

// Unfortunately placeholders are kind of tricky to style
// and these selectors can't be comma separated.
const stylePlaceholder = (color: string) => ({
  '&::placeholder': {
    color,
    // Necessary for Safari
    // '-webkit-text-fill-color': color,
    WebkitTextFillColor: color,
  },
  '&::-moz-placeholder': {
    // Firefox has <1 `opacity` on its placeholders,
    // so we hafta override it.
    opacity: 1,
    color,
  },
});

const InputColors = {
  grey100: {
    backgroundColor: Colors.grey100,
  },
  white: {
    backgroundColor: Colors.white,
  },
};

const Styles = labelStyles(__filename, {
  root: (props: Props) => [
    {
      display: 'grid',
      gridTemplateAreas: '"iconBefore input iconAfter"',
      // For `Tooltip` positioning
      position: 'relative',
      // Defined here so `boxShadow` behaves as expected.
      borderRadius: 10,
    },

    withModifier(props.size, {
      gridTemplateColumns: `${sizeToHeight[props.size]}px 1fr ${
        sizeToHeight[props.size]
      }px`,
    }),

    props.shadow &&
      withModifier(props.shadow, {
        boxShadow: Shadows[props.shadow],
      }),

    props.borderRadiusSide &&
      withModifier(`borderRadiusSide-${props.borderRadiusSide}`, {
        borderRadius: {
          all: 10,
          left: '10px 0 0 10px',
          right: '0 10px 10px 0',
        }[props.borderRadiusSide],
      }),
  ],

  input: (props: Props, derivedState: DerivedState) => [
    {
      ...Typography.altText900,
      fontWeight: 600,
      backgroundColor: Colors.grey100,
      color: Colors.black,
      // Defined on `root` so `root`'s `boxShadow` behaves as expected.
      // This node uses `boxShadow` for its focus state.
      borderRadius: 'inherit',
      // Stretch all the way across. `-1` would be a better
      // value than `4` but Safari doesn't like it.
      gridArea: '1 / 1 / 4 / 4',
      transitionDuration: Animation.transitionDuration,
      // Only transition specific properties because some,
      // like `padding`, will change but shouldn't transition.
      transitionProperty: 'background-color, color, box-shadow',
      transitionTimingFunction: 'ease',

      ...stylePlaceholder(Colors.grey700),

      // Bump font size for devices that don't support hovering
      // to prevent the fields from automatically zooming in on those
      // devices. 16px is defined at the OS level of these devices,
      // specifically iOS.
      '@media (hover: none)': {
        fontSize: 16,
      },

      // Hides additional "clear selection" X icon in Edge.
      // https://zappy.zapier.com/F693DF53-79B7-4857-AADE-73E9BF3011DB.png
      '&::-ms-clear': {
        display: 'none',
      },

      // Hides additional "clear selection" X icon in Safari.
      '&::-webkit-search-cancel-button': {
        display: 'none',
      },

      // Hide selection when `[readonly]` since the text can't
      // be edited, but the selection makes it look like it can be.
      '&[readonly]::selection': {
        backgroundColor: 'transparent',
      },
    },

    withModifier(props.color, {
      backgroundColor: InputColors[props.color].backgroundColor,
    }),

    derivedState.isHovered &&
      withModifier('hovered', {
        color: Colors.blue500,
        ...stylePlaceholder(Colors.blue500),
      }),

    derivedState.isFocused &&
      withModifier('focused', {
        color: Colors.black,
        backgroundColor: Colors.blue100,
        boxShadow: `0 0 0 2px ${Colors.blue500}`,
        ...stylePlaceholder(Colors.blue500),
      }),

    props.isErrored && [
      withModifier('errored', {
        backgroundColor: Colors.red100,
        color: Colors.black,
        ...stylePlaceholder(Colors.red500),
      }),
      derivedState.isFocused &&
        withModifier('focused', {
          boxShadow: `0 0 0 2px ${Colors.red500}`,
          color: Colors.black,
        }),
    ],

    props.isDisabled &&
      withModifier('disabled', {
        backgroundColor: Colors.grey100,
        boxShadow: 'none',
        color: Colors.grey700,
        opacity: 0.6,
        ...stylePlaceholder(Colors.grey700),
      }),

    props.size === 'small' &&
      withModifier(props.size, {
        height: sizeToHeight[props.size],
        padding: '8px 15px',
      }),

    props.size === 'medium' &&
      withModifier(props.size, {
        // One of the rare departures from `Typography`.
        fontSize: 16,
        lineHeight: '25px',
        height: sizeToHeight[props.size],
        padding: '10px 20px',
      }),

    (derivedState.hasIconBefore || derivedState.hasIconAfter) &&
      withModifier('hasIcon', {
        paddingLeft: derivedState.hasIconBefore && sizeToHeight[props.size],
        paddingRight: derivedState.hasIconAfter && sizeToHeight[props.size],
      }),
  ],

  icon: (
    props: Props,
    derivedState: DerivedState,
    iconOpts: { position: 'before' | 'after' }
  ) => [
    {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: Colors.grey700,
      position: 'relative',
      zIndex: 1,
    },

    // Purely for Safari because it doesn't stretch grid
    // items correctly.
    withModifier(props.size, {
      height: sizeToHeight[props.size],
    }),

    withModifier(iconOpts.position, {
      gridArea: iconOpts.position === 'after' ? 'iconAfter' : 'iconBefore',
    }),

    ((!props.canClickIconBefore && iconOpts.position === 'before') ||
      (!props.canClickIconAfter && iconOpts.position === 'after')) &&
      withModifier('disable-click', {
        pointerEvents: 'none',
      }),

    (derivedState.isHovered || derivedState.isFocused) &&
      withModifier('hovered-or-focused', {
        color: Colors.blue500,
      }),

    props.isErrored &&
      withModifier('errored', {
        color: Colors.red500,
      }),

    props.isDisabled &&
      withModifier('disabled', {
        color: Colors.grey700,
        opacity: 0.6,
      }),
  ],
});

/**
 * Renders a wrapper node that may render icons and tooltips. Within
 * that node `children` are rendered as a function which should return
 * some sort of input or input-like element.
 */
const Input = (props: Props) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipId] = useState(
    props.id ? `${props.id}-tooltip` : uniqueId(`${__filename}-tooltip-`)
  );
  const shouldRenderTooltip = props.isDisabled && (isHovered || isFocused);
  const renderIconProps = {
    iconSize: sizeToIconSize[props.size],
    isFocused,
    isHovered,
  };
  const iconBefore =
    props.renderIconBefore && props.renderIconBefore(renderIconProps);
  const iconAfter =
    props.renderIconAfter && props.renderIconAfter(renderIconProps);
  const derivedState = {
    hasIconBefore: !!iconBefore,
    hasIconAfter: !!iconAfter,
    isFocused,
    isHovered,
  };

  return (
    <div
      css={Styles.root(props)}
      onBlur={() => setIsFocused(false)}
      onClick={props.onClick}
      onFocus={() => setIsFocused(true)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="presentation"
    >
      {iconBefore && (
        <div css={Styles.icon(props, derivedState, { position: 'before' })}>
          {iconBefore}
        </div>
      )}
      {props.children(
        {
          'aria-activedescendant': props.ariaActiveDescendant,
          'aria-controls': props.ariaControls,
          'aria-describedby': shouldRenderTooltip
            ? tooltipId
            : props.ariaDescribedBy,
          'aria-invalid': props.isErrored,
          'aria-label': props.ariaLabel,
          'aria-labelledby': props.ariaLabelledBy,
          'aria-required': props.ariaRequired,
          autoComplete: props.autoComplete,
          autoFocus: props.autoFocus,
          css: Styles.input(props, derivedState),
          disabled: props.isDisabled,
          id: props.id,
          maxLength: props.maxLength,
          name: props.name,
          onBlur: props.onBlur,
          onChange: props.onChange,
          onFocus: props.onFocus,
          onKeyDown: props.onKeyDown,
          placeholder: props.placeholder,
          readOnly: props.isReadonly && 'readonly',
          required: props.isRequired,
          role: props.role,
          value: props.value,
        },
        derivedState
      )}
      {iconAfter && (
        <div css={Styles.icon(props, derivedState, { position: 'after' })}>
          {iconAfter}
        </div>
      )}
      {shouldRenderTooltip && (isFocused || isHovered) && (
        <Tooltip id={tooltipId} position="south">
          {props.disabledText}
        </Tooltip>
      )}
    </div>
  );
};

Input.defaultProps = {
  canClickIconBefore: true,
  canClickIconAfter: true,
  color: 'grey100',
  disabledText: 'Disabled',
  size: 'small',
};

export default Input;
